/**
 * Money is always integer minor units (kobo for NGN) plus a currency code.
 * Never floats, never implicit conversion between currencies.
 * Safe-integer kobo covers ~₦90 trillion — far beyond any SME's books.
 */

export const NGN = 'NGN';

export interface Money {
  /** Integer minor units (kobo when currency is NGN). */
  readonly amountMinor: number;
  /** ISO 4217 code. */
  readonly currency: string;
}

export function money(amountMinor: number, currency: string = NGN): Money {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new TypeError(`Money amount must be a safe integer of minor units, got ${amountMinor}`);
  }
  return { amountMinor, currency };
}

/** Naira (or major units) → Money. Only for literals/tests; pipeline code stays in minor units. */
export function fromMajor(amountMajor: number, currency: string = NGN): Money {
  const minor = Math.round(amountMajor * 100);
  if (!Number.isSafeInteger(minor) || Math.abs(amountMajor * 100 - minor) > 1e-6) {
    throw new TypeError(`Amount ${amountMajor} does not convert cleanly to minor units`);
  }
  return money(minor, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new TypeError(`Currency mismatch: ${a.currency} vs ${b.currency} — never convert implicitly`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function sum(items: readonly Money[], currency: string = NGN): Money {
  return items.reduce((acc, m) => add(acc, m), money(0, currency));
}

export function equals(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amountMinor === b.amountMinor;
}

export function isNegative(m: Money): boolean {
  return m.amountMinor < 0;
}

export function isZero(m: Money): boolean {
  return m.amountMinor === 0;
}

const CURRENCY_SYMBOLS: Record<string, string> = { NGN: '₦' };

/**
 * "₦45,000" for whole amounts, "₦45,000.50" when kobo are present.
 * Tone contract: numbers concrete, no noise — whole naira never shows ".00".
 */
export function formatMoney(m: Money): string {
  const symbol = CURRENCY_SYMBOLS[m.currency] ?? `${m.currency} `;
  const negative = m.amountMinor < 0;
  const abs = Math.abs(m.amountMinor);
  const major = Math.floor(abs / 100);
  const minor = abs % 100;
  const majorStr = major.toLocaleString('en-NG');
  const body = minor === 0 ? majorStr : `${majorStr}.${String(minor).padStart(2, '0')}`;
  return `${negative ? '-' : ''}${symbol}${body}`;
}

/**
 * An amount the way people type it — "6000", "6,000", "6k", "₦6,000",
 * "N6000", "1.5k", "2.5m", "96000 naira" — in kobo. Null when it isn't one.
 * Never a float out: the result is rounded to whole kobo.
 */
export function parseNaira(text: string): number | null {
  // Commas only as thousands separators ("6,000", never "60,00" or "6000,"); at most two decimals.
  const m = /^\s*(?:₦|n(?:gn)?\s*)?\s*(\d{1,3}(?:,\d{3})+|\d+)(\.\d{1,2})?\s*([km])?\s*(?:naira)?\s*$/i.exec(text);
  if (!m) return null;
  const digits = m[1]!.replace(/,/g, '') + (m[2] ?? '');
  const multiplier = m[3]?.toLowerCase() === 'm' ? 1_000_000 : m[3]?.toLowerCase() === 'k' ? 1_000 : 1;
  const kobo = Math.round(Number(digits) * multiplier * 100);
  return Number.isSafeInteger(kobo) ? kobo : null;
}
